import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  CheckCircle,
  Loader2,
  AlertCircle,
  Users,
  MapPin,
  Briefcase,
  Star,
  ChevronRight,
  X,
} from 'lucide-react'
import matchingService from '@/services/matchingService'
import recruiterJobService from '@/services/recruiterJobService'
import { toast } from '@/stores/toastStore'
import { useAuth } from '@/stores/authStore'
import { useMatchingStore } from '@/stores/matchingStore'
import type { CreateMatchRequest, MatchJobStatus, ContractType } from '@/types'
import { JobDescriptionForm } from '@/components/recruiter/ai/JobDescriptionForm'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import QuotaBar from '@/components/recruiter/QuotaBar'
import { useTranslation } from 'react-i18next'

const CONTRACT_TYPE_KEYS: Record<ContractType, string> = {
  FULL_TIME: 'contractType.fullTime',
  HALF_TIME: 'contractType.halfTime',
  CONTRACTOR: 'contractType.contractor',
  INTERNSHIP: 'contractType.internship',
  PFE: 'contractType.pfe',
}

export default function CreateAIMatch() {
  const navigate = useNavigate()
  const { t } = useTranslation('aiMatching')
  const { refreshUser } = useAuth()
  const [isCreating, setIsCreating] = useState(false)
  const [isPolling, setIsPolling] = useState(false)
  const [matchJobStatus, setMatchJobStatus] = useState<MatchJobStatus | null>(null)
  const [currentRequest, setCurrentRequest] = useState<CreateMatchRequest | null>(null)
  const [pollingProgress, setPollingProgress] = useState('')
  const [embeddingsError, setEmbeddingsError] = useState<{ jobId: string } | null>(null)
  const [isReprocessing, setIsReprocessing] = useState(false)
  const [noCandidatesFound, setNoCandidatesFound] = useState(false)
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const { previewData, isLoadingPreview, setPreviewData, setLoadingPreview, setPreviewError } =
    useMatchingStore()

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
      }
    }
  }, [])

  const handleReprocess = async (jobId: string) => {
    setIsReprocessing(true)
    try {
      await recruiterJobService.reprocessJob(jobId)
      toast.success('Job reprocessed — embeddings are now ready. You can try matching again.')
      setEmbeddingsError(null)
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } }
      toast.error(err.response?.data?.error || 'Failed to reprocess job')
    } finally {
      setIsReprocessing(false)
    }
  }

  const handlePreview = async (request: CreateMatchRequest) => {
    setCurrentRequest(request)
    setEmbeddingsError(null)
    setLoadingPreview(true)
    setPreviewError(null)

    try {
      const result = await matchingService.previewMatch(request)
      setPreviewData(result)
    } catch (error: any) {
      if (error.response?.data?.error?.code === 'EMBEDDINGS_NOT_READY') {
        setEmbeddingsError({ jobId: request.recruiterJobId })
      } else if (error.response?.data?.error?.code === 'NO_RELEVANT_SKILLS') {
        const errorMessage =
          error.response?.data?.error?.message ||
          'None of the skills extracted from this job description match skills in our database. Please update the job description to include recognisable skills before running AI match.'
        toast.error(errorMessage, 6000)
        setPreviewError(errorMessage)
      } else {
        const errorMessage = error.response?.data?.error?.message || 'Failed to load preview'
        toast.error(errorMessage)
        setPreviewError(errorMessage)
      }
    } finally {
      setLoadingPreview(false)
    }
  }

  // Start polling for job status
  const startPolling = async (jobId: string) => {
    setIsPolling(true)
    setPollingProgress('Initializing AI matching...')

    let errorCount = 0
    const MAX_ERRORS = 5

    const pollJobStatus = async () => {
      try {
        const status = await matchingService.getMatchJobStatus(jobId)
        errorCount = 0 // Reset error count on success
        setMatchJobStatus(status)

        if (status.status === 'pending') {
          setPollingProgress('Queued - waiting to start...')
        } else if (status.status === 'processing') {
          setPollingProgress('AI is analyzing candidates...')
        } else if (status.status === 'completed') {
          // Stop polling
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current)
          }

          setIsPolling(false)
          setIsCreating(false)
          setPreviewData(null)

          if (status.errorCode === 'NO_CANDIDATES_FOUND') {
            // Quota was automatically refunded — show friendly state, no navigation
            setNoCandidatesFound(true)
            await refreshUser()
          } else {
            // Normal completion — navigate to results
            toast.success('Match completed!', 4000)
            if (status.result?.matchId) {
              await refreshUser()
              navigate(`/recruiter/ai-matching/results/${status.result.matchId}`)
            }
          }
        } else if (status.status === 'failed') {
          // Stop polling
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current)
          }

          setIsPolling(false)
          setIsCreating(false)
          setPreviewData(null)

          const errorMessage = status.error?.message || 'Match job failed'
          toast.error(errorMessage, 5000)

          // Refresh user to get refunded credits
          await refreshUser()
        }
      } catch (error: unknown) {
        errorCount++

        if (errorCount >= MAX_ERRORS) {
          // Stop polling after too many errors
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current)
          }

          setIsPolling(false)
          setIsCreating(false)
          setPreviewData(null)

          toast.error(
            'Lost connection to server. Please refresh and check your match status.',
            8000
          )
        }
      }
    }

    // Poll immediately
    await pollJobStatus()

    // Then poll every 2 seconds
    pollingIntervalRef.current = setInterval(pollJobStatus, 2000)
  }

  const createAndPollMatch = async (request: CreateMatchRequest) => {
    setIsCreating(true)
    setEmbeddingsError(null)
    try {
      const result = await matchingService.createMatch(request)

      toast.info('Match job created! Processing...', 3000)

      // Start polling for job status
      await startPolling(result.jobId)
    } catch (error: unknown) {
      const apiError = error as {
        response?: {
          status?: number
          data?: {
            error?: {
              message?: string
              code?: string
              requiredCredits?: number
              balance?: { remaining?: number }
            }
            message?: string
          }
        }
      }

      if (apiError.response?.data?.error?.code === 'EMBEDDINGS_NOT_READY') {
        setEmbeddingsError({ jobId: request.recruiterJobId })
        setPreviewData(null)
      } else if (apiError.response?.data?.error?.code === 'NO_RELEVANT_SKILLS') {
        const msg =
          apiError.response?.data?.error?.message ||
          'None of the skills extracted from this job description match skills in our database. Please update the job description to include recognisable skills before running AI match.'
        toast.error(msg, 6000)
        setPreviewData(null)
      } else if (apiError.response?.status === 402) {
        const msg =
          apiError.response?.data?.error?.message ||
          apiError.response?.data?.message ||
          'AI search quota exhausted. Purchase a new pack to continue.'
        toast.error(msg, 5000)
      } else {
        const errorMessage =
          apiError.response?.data?.error?.message ||
          apiError.response?.data?.message ||
          'Failed to create AI match'
        toast.error(errorMessage)
      }

      setIsCreating(false)
      setPreviewData(null)
    }
  }

  const handleConfirmMatch = async () => {
    if (!currentRequest) return
    await createAndPollMatch(currentRequest)
  }

  const handleCreateMatch = async (request: CreateMatchRequest) => {
    setCurrentRequest(request)
    await createAndPollMatch(request)
  }

  const handleCancelPreview = () => {
    setPreviewData(null)
    setCurrentRequest(null)
  }

  // Show no-candidates-found state (quota was refunded)
  if (noCandidatesFound) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="text-center py-12">
          <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">No Matching Candidates Found</h2>
          <p className="text-gray-600 mb-4">
            No candidates in our database matched the criteria for this job.
          </p>
          <div className="flex justify-center mb-6">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 border border-green-200">
              <CheckCircle className="w-4 h-4" />
              Quota Refunded
            </span>
          </div>
          <p className="text-sm text-gray-500 mb-8">
            Try broadening your filters or reprocessing the job description with more recognisable
            skills.
          </p>
          <div className="flex gap-3 justify-center">
            <Button
              onClick={() => {
                setNoCandidatesFound(false)
                setMatchJobStatus(null)
              }}
              variant="primary"
            >
              Try Again
            </Button>
            <Button onClick={() => navigate('/recruiter/ai-matching')} variant="secondary">
              Back to AI Matching
            </Button>
          </div>
        </Card>
      </div>
    )
  }

  // Show processing/polling state
  if (isPolling && matchJobStatus) {
    const isCompleted = matchJobStatus.status === 'completed'

    if (isCompleted && matchJobStatus.result) {
      return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card className="text-center py-12">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">AI Matching Complete!</h2>
            <p className="text-gray-600 mb-6">
              Found{' '}
              <span className="font-semibold text-blue-600">
                {matchJobStatus.result.totalCandidates}
              </span>{' '}
              matching candidates
            </p>
            <p className="text-sm text-gray-500 mb-6">Redirecting to results...</p>
            <Button
              onClick={() =>
                navigate(`/recruiter/ai-matching/results/${matchJobStatus.result!.matchId}`)
              }
              variant="primary"
            >
              View Results Now
            </Button>
          </Card>
        </div>
      )
    }

    // Processing state
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="text-center py-12">
          <Loader2 className="w-16 h-16 text-blue-500 mx-auto mb-4 animate-spin" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Processing AI Match...</h2>
          <p className="text-gray-600 mb-6">{pollingProgress}</p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 max-w-md mx-auto">
            <p className="text-sm text-gray-700">
              Status: <span className="font-semibold capitalize">{matchJobStatus.status}</span>
            </p>
          </div>
          <p className="text-xs text-gray-500">
            This usually takes 5-10 seconds. Please don't close this page.
          </p>
        </Card>
      </div>
    )
  }

  return (
    <>
      <QuotaBar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Button
            onClick={() => navigate('/recruiter/ai-matching')}
            variant="secondary"
            className="mb-4 inline-flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to AI Matching
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">Create New AI Match</h1>
          <p className="mt-2 text-gray-600">
            Select one of your jobs and let AI find the best-matching candidates from our database
          </p>
        </div>

        {/* Info Box */}
        <Card className="mb-6 bg-blue-50 border-blue-200">
          <div className="p-4">
            <h3 className="font-semibold text-blue-900 mb-2">How AI Matching Works</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Select a job you've created — AI uses its description to find candidates</li>
              <li>• AI analyzes the job requirements and candidate profiles</li>
              <li>
                • Candidates are ranked by overall match score (skills, experience, education)
              </li>
              <li>• Review detailed explanations for each match</li>
              <li>• Provide feedback to improve future matches</li>
            </ul>
          </div>
        </Card>

        {/* Form */}
        <Card>
          <div className="p-6">
            <JobDescriptionForm
              onSubmit={handleCreateMatch}
              onPreview={handlePreview}
              isLoading={isCreating}
              isLoadingPreview={isLoadingPreview}
              embeddingsError={embeddingsError}
              onReprocess={handleReprocess}
              isReprocessing={isReprocessing}
            />
          </div>
        </Card>

        {/* Inline Preview Section */}
        {previewData &&
          (() => {
            const {
              totalCandidates = 0,
              matchLimit = 0,
              sampleCandidates = [],
              poolStatistics = {
                totalPoolSize: 0,
                filtersApplied: [],
                experienceRange: { min: 0, max: 0 },
              },
              pricing = { totalCost: 0, costPerMatch: 0, breakdown: [], value: '' },
            } = previewData

            return (
              <Card className="mt-6">
                <div className="p-6 space-y-5">
                  {/* Header */}
                  <div className="flex items-center justify-between border-b border-gray-200 pb-4">
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">{t('preview.title')}</h2>
                      <p className="text-sm text-gray-600 mt-0.5">{t('preview.subtitle')}</p>
                    </div>
                    <button
                      onClick={handleCancelPreview}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      disabled={isCreating}
                      aria-label={t('preview.cancelButton')}
                    >
                      <X className="w-5 h-5 text-gray-500" />
                    </button>
                  </div>

                  {/* Pricing banner */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-blue-900">{t('preview.pricing')}</p>
                        {pricing.value && (
                          <p className="text-sm text-blue-700 mt-1">{pricing.value}</p>
                        )}
                        <p className="text-xs text-blue-600 mt-1">
                          {t('preview.costPerMatch')}: {pricing.costPerMatch}{' '}
                          {t('preview.creditsUnit')}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-3xl font-bold text-blue-900">{pricing.totalCost}</div>
                        <div className="text-sm text-blue-700">{t('preview.creditsUnit')}</div>
                      </div>
                    </div>
                    {pricing.breakdown.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-blue-200 space-y-1">
                        {pricing.breakdown.map((tier, idx) => (
                          <div key={idx} className="flex justify-between text-xs text-blue-700">
                            <span>
                              {tier.tier} × {tier.rate} {t('preview.creditsUnit')}
                            </span>
                            <span className="font-medium">
                              {tier.cost} {t('preview.creditsUnit')}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Pool statistics */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                      <Users className="w-4 h-4 text-gray-600" />
                      {t('preview.poolStatistics')}
                    </h3>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-white rounded-lg p-3 border border-gray-200 text-center">
                        <div className="text-2xl font-bold text-gray-900">{totalCandidates}</div>
                        <div className="text-xs text-gray-600 mt-0.5">
                          {t('preview.totalCandidates')}
                        </div>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-gray-200 text-center">
                        <div className="text-2xl font-bold text-gray-900">{matchLimit}</div>
                        <div className="text-xs text-gray-600 mt-0.5">
                          {t('preview.matchLimit')}
                        </div>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-gray-200 text-center">
                        <div className="text-2xl font-bold text-gray-900">
                          {poolStatistics.experienceRange.min}–{poolStatistics.experienceRange.max}
                        </div>
                        <div className="text-xs text-gray-600 mt-0.5">
                          {t('preview.experienceRange')}
                        </div>
                      </div>
                    </div>
                    {poolStatistics.filtersApplied.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-gray-700 mb-2">
                          {t('preview.filtersApplied')}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {poolStatistics.filtersApplied.map((filter, idx) => (
                            <span
                              key={idx}
                              className="bg-white border border-gray-300 rounded-full px-3 py-1 text-xs text-gray-700"
                            >
                              {filter}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Sample candidates */}
                  <div className="space-y-3">
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                      <Star className="w-4 h-4 text-gray-600" />
                      {t('preview.sampleCandidates')}
                      <span className="text-xs font-normal text-gray-500">
                        ({t('preview.sampleCandidatesNote')})
                      </span>
                    </h3>
                    {sampleCandidates.length > 0 ? (
                      <div className="space-y-3">
                        {sampleCandidates.map((candidate, idx) => (
                          <div
                            key={idx}
                            className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm"
                          >
                            <p className="font-medium text-gray-900 truncate">
                              {candidate.jobTitle ?? t('preview.unknownPosition')}
                            </p>
                            <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-gray-600">
                              {candidate.location && (
                                <span className="flex items-center gap-1">
                                  <MapPin className="w-3 h-3 shrink-0" />
                                  {candidate.location}
                                </span>
                              )}
                              <span className="flex items-center gap-1">
                                <Briefcase className="w-3 h-3 shrink-0" />
                                {t('preview.yearsExp', { count: candidate.yearsExperience })}
                              </span>
                              {candidate.employmentStatus && (
                                <span className="inline-block bg-gray-100 text-gray-700 text-xs px-2 py-0.5 rounded">
                                  {t(`employmentStatus.${candidate.employmentStatus}`)}
                                </span>
                              )}
                              {candidate.contractType && (
                                <span className="inline-block bg-gray-100 text-gray-700 text-xs px-2 py-0.5 rounded">
                                  {t(CONTRACT_TYPE_KEYS[candidate.contractType])}
                                </span>
                              )}
                            </div>
                            {candidate.skills.length > 0 && (
                              <div className="flex flex-wrap gap-1.5 mt-2">
                                {candidate.skills.map((skill, sIdx) => (
                                  <span
                                    key={sIdx}
                                    className="bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-2.5 py-0.5 text-xs font-medium"
                                  >
                                    {skill}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                        {totalCandidates > sampleCandidates.length && (
                          <div className="flex items-center justify-center gap-1 text-sm text-gray-500 py-1">
                            <ChevronRight className="w-4 h-4" />
                            {t('preview.moreCandidates', {
                              count: totalCandidates - sampleCandidates.length,
                            })}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
                        <p className="text-gray-600 font-medium">{t('preview.noCandidates')}</p>
                        <p className="text-sm text-gray-500 mt-1">
                          {t('preview.noCandidatesDescription')}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-between gap-3 pt-4 border-t border-gray-200">
                    <button
                      onClick={handleCancelPreview}
                      disabled={isCreating}
                      className="px-5 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                    >
                      {t('preview.cancelButton')}
                    </button>
                    <button
                      onClick={handleConfirmMatch}
                      disabled={isCreating || totalCandidates === 0}
                      className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isCreating
                        ? t('preview.confirming')
                        : t('preview.confirmButton', { count: pricing.totalCost })}
                    </button>
                  </div>
                </div>
              </Card>
            )
          })()}
      </div>
    </>
  )
}
